/**
 * GLDN Cart Orphan Cleanup
 *
 * Personalizer add-on items (`_tag: true`) belong to a parent builder
 * line via a shared `Builder` id. When the parent is removed, Shopify
 * only removes that one line — the add-ons stay stranded with no UI to
 * remove them. Live behavior is to remove the entire set together.
 *
 * Two layers:
 *
 * 1. Network interceptor (primary). Rewrites outgoing /cart/change.js
 *    POSTs that target a builder parent (quantity 0) into a single
 *    /cart/update.js call that removes the parent + every tag with the
 *    same Builder id, preserving the original `sections` /
 *    `sections_url` so Horizon's native cart-items handler processes
 *    the response identically — including the drawer's hydration-mode
 *    morph that keeps `<dialog open>` intact. This is what makes the
 *    cart drawer stay open through the cleanup, matching the
 *    drawer's behavior when removing a plain product.
 *
 * 2. Reactive sweep (safety net). On every cart:update (not sourced
 *    from us) and once on DOMContentLoaded, fetch /cart.js and remove
 *    any orphan tags whose Builder id has no non-tag sibling. Covers
 *    pre-existing orphans (cross-session, multi-tab, server-side
 *    mutations) and any removal path the interceptor missed.
 */
(function () {
  if (!window.fetch) return;

  var SOURCE_ID = 'gldn-cart-orphan-cleanup';
  var cleaning = false;

  /* ---------- shared helpers ---------- */

  function getProp(item, name) {
    if (!item || !item.properties) return null;
    var p = item.properties[name];
    return p == null ? null : p;
  }

  function isTag(item) {
    var v = getProp(item, '_tag');
    return v === true || v === 'true';
  }

  function builderIdOf(item) {
    var v = getProp(item, 'Builder');
    return v ? String(v) : '';
  }

  function findOrphanKeys(items) {
    if (!Array.isArray(items)) return [];
    var parents = Object.create(null);
    items.forEach(function (item) {
      var bid = builderIdOf(item);
      if (!bid) return;
      if (!isTag(item)) parents[bid] = true;
    });
    var keys = [];
    items.forEach(function (item) {
      if (!isTag(item)) return;
      var bid = builderIdOf(item);
      if (!bid || !parents[bid]) {
        if (item.key) keys.push(item.key);
      }
    });
    return keys;
  }

  function findTagsByBuilder(items, builderId) {
    var keys = [];
    if (!Array.isArray(items) || !builderId) return keys;
    items.forEach(function (item) {
      if (!isTag(item)) return;
      if (builderIdOf(item) !== builderId) return;
      if (item.key) keys.push(item.key);
    });
    return keys;
  }

  function isCartChangeURL(url) {
    if (!url) return false;
    if (typeof url !== 'string') {
      try { url = url.url || String(url); } catch (e) { return false; }
    }
    return /\/cart\/change(\.js)?(\?|$)/.test(url);
  }

  function isCartUpdateURL(url) {
    if (!url) return false;
    if (typeof url !== 'string') {
      try { url = url.url || String(url); } catch (e) { return false; }
    }
    return /\/cart\/update(\.js)?(\?|$)/.test(url);
  }

  function readBodyAsString(body) {
    if (body == null) return null;
    if (typeof body === 'string') return body;
    /* FormData / URLSearchParams etc. — not what Horizon sends, but be defensive */
    try {
      if (body instanceof URLSearchParams) return body.toString();
    } catch (e) {}
    return null;
  }

  function parseJSONBody(body) {
    var str = readBodyAsString(body);
    if (!str || str.charAt(0) !== '{') return null;
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  /* ---------- layer 1: interceptor ----------
   *
   * Native removal: POST /cart/change.js  { line, quantity: 0, sections, sections_url }
   * Custom remove (gldn-cart-builder-actions.js): POST /cart/change.js  { id: lineKey, quantity: 0 }
   *
   * If the targeted line is a builder parent, rewrite to:
   *   POST /cart/update.js  { updates: { parentKey: 0, tagKey: 0, ... }, sections, sections_url }
   *
   * Horizon's cart-items handler reads `parsedResponseText.sections[sectionId]`
   * — both endpoints return the same shape, so the native path runs unchanged.
   */

  async function rewriteRequestIfBuilderRemove(originalUrl, init, originalFetch) {
    var parsed = parseJSONBody(init && init.body);
    if (!parsed) return null;
    if (parsed.quantity !== 0 && parsed.quantity !== '0') return null;

    /* Get current cart so we can identify the line being removed and its
     * tag siblings. */
    var cart;
    try {
      var res = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res || !res.ok) return null;
      cart = await res.json();
    } catch (e) {
      return null;
    }
    if (!cart || !Array.isArray(cart.items)) return null;

    var target = null;
    if (parsed.id != null) {
      /* Custom remove button path — id is the line key. */
      target = cart.items.find(function (i) { return i.key === parsed.id; });
    } else if (typeof parsed.line === 'number' && parsed.line > 0) {
      /* Native quantity stepper / X path — 1-indexed line position. */
      target = cart.items[parsed.line - 1] || null;
    }
    if (!target) return null;

    /* Only rewrite when removing a builder PARENT (has Builder id, not a tag).
     * Tag removals don't need cascading. */
    var bid = builderIdOf(target);
    if (!bid || isTag(target)) return null;

    var tagKeys = findTagsByBuilder(cart.items, bid);
    if (!tagKeys.length) return null; /* parent alone — let native flow handle it */

    /* /cart/update.js with `updates` as an OBJECT keys-by-line-item-key
     * is ambiguous when multiple cart lines share a variant_id (e.g. a
     * duplicate + the original of the same personalized piece) — Shopify
     * collapses the colon-separated key down to its variant_id prefix
     * and removes EVERY line of that variant, not just the targeted one.
     * Empirically: removing one duplicate's main wiped the original main
     * (same variant) AND the original's add-ons too.
     *
     * Use the POSITIONAL ARRAY form instead. `updates: [q0, q1, ...]`
     * is indexed by cart.items[i] position, so each line is addressed
     * unambiguously regardless of variant_id collisions. Preserve every
     * other line's current quantity; zero out only the lines we mean to
     * remove. */
    var killKeys = Object.create(null);
    killKeys[target.key] = true;
    tagKeys.forEach(function (k) { killKeys[k] = true; });
    var updates = cart.items.map(function (item) {
      return killKeys[item.key] ? 0 : item.quantity;
    });

    var newBody = { updates: updates };
    if (parsed.sections) newBody.sections = parsed.sections;
    if (parsed.sections_url) newBody.sections_url = parsed.sections_url;

    var newInit = Object.assign({}, init, {
      method: 'POST',
      body: JSON.stringify(newBody)
    });
    /* Preserve headers exactly — fetchConfig sets Content-Type: application/json
     * and Accept: application/json, both correct for /cart/update.js. */

    /* Origin-relative URL — Shopify accepts both /cart/update and /cart/update.js. */
    var newUrl = '/cart/update.js';
    return originalFetch(newUrl, newInit);
  }

  var originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (!isCartChangeURL(url)) return originalFetch(input, init);

      var method = (init && init.method) || (input && input.method) || 'GET';
      if (String(method).toUpperCase() !== 'POST') return originalFetch(input, init);
      if (!init || init.body == null) return originalFetch(input, init);

      /* Async rewrite path. The returned Promise resolves to the rewritten
       * request's Response (or to the original request's Response if the
       * line isn't a builder parent). */
      return (async function () {
        try {
          var rewritten = await rewriteRequestIfBuilderRemove(url, init, originalFetch);
          if (rewritten) return rewritten;
        } catch (e) {
          try { console.warn('[gldn-cart-orphan-cleanup] interceptor failed, passing through:', e); } catch (_) {}
        }
        return originalFetch(input, init);
      })();
    } catch (e) {
      try { console.warn('[gldn-cart-orphan-cleanup] fetch wrapper failed:', e); } catch (_) {}
      return originalFetch(input, init);
    }
  };

  /* ---------- layer 2: reactive safety-net sweep ---------- */

  function sectionIdsToRefresh() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function dispatchCartUpdate(cartData) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData || null,
        sourceId: SOURCE_ID,
        data: {
          source: SOURCE_ID,
          sections: cartData && cartData.sections ? cartData.sections : undefined,
          itemCount: cartData && typeof cartData.item_count === 'number' ? cartData.item_count : undefined
        }
      }
    }));
  }

  async function sweep() {
    if (cleaning) return;

    // gldn95 (#405): the sweep runs on DOMContentLoaded and after cart updates,
    // reading the same body every other cart script reads. Share the coalesced
    // snapshot; cart mutations invalidate it, so an orphan removed elsewhere is
    // never re-read from a stale cart. Falls back to a direct read.
    var cart;
    try {
      if (typeof window.gldnCartSnapshot === 'function') {
        cart = await window.gldnCartSnapshot();
      } else {
        var res = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res || !res.ok) return;
        cart = await res.json();
      }
    } catch (e) { return; }

    var orphanKeys = findOrphanKeys(cart && cart.items);
    if (!orphanKeys.length) return;

    cleaning = true;

    var updates = {};
    orphanKeys.forEach(function (k) { updates[k] = 0; });

    var sectionIds = sectionIdsToRefresh();
    var body = { updates: updates };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      var res2 = await originalFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      var data = res2 && res2.ok ? await res2.json() : null;
      dispatchCartUpdate(data);
    } catch (e) {
      try { console.warn('[gldn-cart-orphan-cleanup] sweep failed:', e); } catch (_) {}
    } finally {
      cleaning = false;
    }
  }

  document.addEventListener('cart:update', function (e) {
    if (e && e.detail && e.detail.sourceId === SOURCE_ID) return;
    sweep();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();
